import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Quotes, Star } from "@phosphor-icons/react";
import { SITE_IMG } from "@/lib/siteImages";
import { TitleBand } from "@/components/site/TitleBand";

export const Eyebrow = ({ children, dark = false, className = "" }) => (
  <span className={`s-eyebrow ${dark ? "s-on-dark" : ""} ${className}`}>
    {children}
  </span>
);

// Pill button — gold (default), outline (on dark photos) or ghost (on light).
export const Btn = ({
  to,
  href,
  children,
  variant = "gold",
  className = "",
  testId,
  arrow = true,
  onClick,
  type,
  ...rest
}) => {
  const base =
    variant === "outline"
      ? "s-ghost-light"
      : variant === "ghost"
        ? "s-ghost"
        : "s-btn";
  const cls = `${base} ${arrow ? "" : "s-no-arrow"} ${className}`;
  const inner = children;
  if (href)
    return (
      <a href={href} data-testid={testId} className={cls} onClick={onClick} {...rest}>
        {inner}
      </a>
    );
  if (to)
    return (
      <Link to={to} data-testid={testId} className={cls} onClick={onClick} {...rest}>
        {inner}
      </Link>
    );
  return (
    <button
      type={type || "button"}
      data-testid={testId}
      className={cls}
      onClick={onClick}
      {...rest}
    >
      {inner}
    </button>
  );
};

export const SectionHead = ({
  eyebrow,
  title,
  sub,
  action,
  dark = false,
  className = "",
  testId,
}) => (
  <div className={`s-reveal ${className}`} data-testid={testId}>
    <TitleBand
      eyebrow={eyebrow}
      heading={title}
      dark={dark}
      bandTestId={testId ? `${testId}-band` : undefined}
    />
    {(sub || action) && (
      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        {sub && (
          <p
            className={`max-w-2xl text-base leading-relaxed ${
              dark ? "text-white/60" : "text-[color:var(--s-mute)]"
            }`}
          >
            {sub}
          </p>
        )}
        {action && <div className="shrink-0">{action}</div>}
      </div>
    )}
  </div>
);

// Dark full-bleed page opener shared by the company pages.
export const PageHero = ({
  image,
  eyebrow,
  title,
  body,
  bodyHtml,
  children,
  testId,
  titleTestId,
  bodyTestId,
  minH = "min-h-[58vh] lg:min-h-[66vh]",
}) => (
  <section
    className={`relative ${minH} overflow-hidden bg-[color:var(--s-dark)]`}
    data-testid={testId}
  >
    {image && (
      <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
    )}
    <span className="absolute inset-0 bg-gradient-to-r from-[#1a1a18]/95 via-[#1a1a18]/70 to-[#1a1a18]/30" />
    <span className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#1a1a18]/80 to-transparent" />
    <span className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#1a1a18] to-transparent" />
    <div
      className={`relative mx-auto flex ${minH} max-w-[2560px] flex-col justify-end px-6 pb-14 pt-36 lg:px-12 lg:pb-20`}
    >
      <div className="s-rise max-w-3xl">
        {eyebrow && <Eyebrow dark>{eyebrow}</Eyebrow>}
        <h1
          className="s-h mt-6 text-4xl text-white sm:text-5xl lg:text-6xl"
          data-testid={titleTestId}
        >
          {title}
        </h1>
        {bodyHtml ? (
          <div
            className="s-prose mt-6 max-w-xl text-base leading-relaxed text-white/75"
            data-testid={bodyTestId}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        ) : (
          body && (
            <p
              className="mt-6 max-w-xl text-base leading-relaxed text-white/75"
              data-testid={bodyTestId}
            >
              {body}
            </p>
          )
        )}
        {children}
      </div>
    </div>
  </section>
);

const usePerView = () => {
  const [per, setPer] = useState(3);
  useEffect(() => {
    const lg = window.matchMedia("(min-width: 1024px)");
    const sm = window.matchMedia("(min-width: 640px)");
    const upd = () => setPer(lg.matches ? 3 : sm.matches ? 2 : 1);
    upd();
    lg.addEventListener("change", upd);
    sm.addEventListener("change", upd);
    return () => {
      lg.removeEventListener("change", upd);
      sm.removeEventListener("change", upd);
    };
  }, []);
  return per;
};

// Dark review slider — three cards a row on desktop, prev/next circles top right.
// Manual quotes come first, then the reviews synced from Google (with attribution).
export const Testimonials = ({
  data,
  testId = "site-testimonials",
  prefix = "site-testimonial",
}) => {
  const google = data?.google || null;
  const items = [
    ...(data?.items || []),
    ...((google?.items || []).map((g) => ({ ...g, google: true }))),
  ];
  const [i, setI] = useState(0);
  if (data?.enabled === false || items.length === 0) return null;
  const idx = (((i % items.length) + items.length) % items.length);
  const cur = items[idx];
  const rating = google?.rating ? Number(google.rating).toFixed(1) : (data?.rating ? String(data.rating) : "");
  const count = google?.count || data?.reviews || "";
  const parts = String(cur.body || "").split(/(?<=[.!?])\s+/);
  const lead = parts.slice(0, 1).join(" ");
  const rest = parts.slice(1).join(" ");
  const role = cur.role || cur.source || (cur.google ? "Verified Google review" : "");
  const heroImg = data?.image || SITE_IMG.walls;
  const stars = Math.max(1, Math.min(5, Number(cur.rating) || 5));
  return (
    <section className="bg-[color:var(--s-bg)] pt-20 sm:pt-28" data-testid={testId}>
      <div className="grid items-stretch gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="relative min-h-[340px] overflow-hidden bg-[color:var(--s-dark)] lg:min-h-[580px]">
          <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
          <span aria-hidden className="pointer-events-none absolute inset-5 border border-white/15" />
          {rating && (
            <div className="absolute bottom-7 right-7 flex items-end gap-3 text-white" data-testid={`${prefix}-rating`}>
              <span className="s-num text-5xl leading-none sm:text-6xl">
                {rating}
                <span className="text-white/45">/5</span>
              </span>
              <span className="mb-1.5 max-w-[9rem] text-xs leading-tight text-white/70">
                Rated by {count ? `${count}+` : "our"} happy clients
              </span>
            </div>
          )}
        </div>

        <div className="s-reveal flex flex-col justify-center px-6 lg:px-0 lg:pr-14">
          <TitleBand
            eyebrow={data.micro || "Reviews"}
            heading={data.heading || "What clients are saying"}
            align="left"
            headingTestId={`${prefix}-heading`}
          />
          <figure className="mt-9" data-testid={`${prefix}-${idx}`}>
            <div className="flex gap-1 text-[color:var(--s-gold-bright)]">
              {Array.from({ length: stars }).map((_, k) => (
                <Star key={k} size={16} weight="fill" />
              ))}
            </div>
            <blockquote className="mt-6 max-w-2xl">
              <p className="s-h text-xl leading-snug text-[color:var(--s-ink)] sm:text-2xl">{lead || cur.body}</p>
              {rest && <p className="mt-5 text-sm leading-relaxed text-[color:var(--s-mute)]">{rest}</p>}
            </blockquote>
            <figcaption className="mt-9 flex items-center justify-between gap-6">
              <div className="flex min-w-0 items-center gap-4">
                {cur.google && cur.photo_uri ? (
                  <img src={cur.photo_uri} alt="" referrerPolicy="no-referrer" className="h-12 w-12 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[color:var(--s-cream)] text-base font-semibold text-[color:var(--s-ink)]">
                    {(cur.title || "–").charAt(0)}
                  </span>
                )}
                <div className="min-w-0">
                  {cur.google && cur.author_uri ? (
                    <a href={cur.author_uri} target="_blank" rel="noreferrer" className="s-h block truncate text-base text-[color:var(--s-ink)] hover:text-[color:var(--s-gold)]">
                      {cur.title}
                    </a>
                  ) : (
                    <div className="s-h truncate text-base text-[color:var(--s-ink)]">{cur.title}</div>
                  )}
                  {role && <div className="mt-1 truncate text-[11px] uppercase tracking-[0.18em] text-[color:var(--s-mute)]">{role}</div>}
                </div>
              </div>
              {items.length > 1 && (
                <div className="flex shrink-0 gap-3">
                  <button
                    type="button"
                    aria-label="Previous review"
                    data-testid={`${prefix}-prev`}
                    onClick={() => setI(idx - 1)}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-ink)] transition-colors duration-300 hover:border-[color:var(--s-ink)] hover:bg-[color:var(--s-ink)] hover:text-white"
                  >
                    <ArrowLeft size={18} weight="bold" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next review"
                    data-testid={`${prefix}-next`}
                    onClick={() => setI(idx + 1)}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-ink)] transition-colors duration-300 hover:border-[color:var(--s-ink)] hover:bg-[color:var(--s-ink)] hover:text-white"
                  >
                    <ArrowRight size={18} weight="bold" />
                  </button>
                </div>
              )}
            </figcaption>
          </figure>
          {rating && (
            <a
              href={google?.maps_uri || "https://www.google.com/maps"}
              target="_blank"
              rel="noreferrer"
              className="mt-9 inline-flex items-center gap-2 text-xs text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]"
              data-testid={`${prefix}-google-summary`}
            >
              <span className="flex gap-0.5 text-[color:var(--s-gold-bright)]">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} size={12} weight={k < Math.round(Number(rating)) ? "fill" : "regular"} />
                ))}
              </span>
              <span className="font-semibold text-[color:var(--s-ink)]">{rating}</span>
              <span>· {count || items.length} Google reviews</span>
              <ArrowUpRight size={12} weight="bold" />
            </a>
          )}
          {google?.items?.length > 0 && (
            <p className="mt-4 text-[11px] leading-relaxed text-[color:var(--s-mute)]" data-testid={`${prefix}-google-note`}>
              Google reviews are shown as provided by Google, ordered by relevance, and are not verified by Google.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

// Full-bleed closing banner — "Reach out today for a free consultation".
export const CtaBanner = ({ cfg = {}, shop = {}, image, testId = "site-cta" }) => {
  if (cfg.enabled === false) return null;
  return (
    <section
      className="relative overflow-hidden bg-[color:var(--s-dark)]"
      data-testid={testId}
    >
      {image && (
        <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}
      <span className="absolute inset-0 bg-gradient-to-r from-[#1a1a18]/95 via-[#1a1a18]/82 to-[#1a1a18]/55" />
      <div className="relative mx-auto max-w-[2560px] px-6 py-24 sm:py-32 lg:px-12">
        <div className="s-reveal max-w-2xl">
          <Eyebrow dark>{cfg.micro || "Schedule a call"}</Eyebrow>
          <h2 className="s-h mt-6 text-3xl text-white sm:text-4xl lg:text-5xl">
            {cfg.heading || "Reach out today for a free consultation!"}
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75">
            {cfg.body ||
              "Send your plans or a rough sketch with your square footage. We come back with a written quote — no obligation, no pushy follow-ups."}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Btn to="/contact" testId="cta-quote">
              {cfg.cta_label || "Schedule a call"}
            </Btn>
            {shop.phone && (
              <Btn
                href={`tel:${shop.phone_link || shop.phone}`}
                variant="outline"
                arrow={false}
                testId="cta-phone"
              >
                {shop.phone}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
